import type { RefObject } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(useGSAP, ScrollTrigger);

type LandingMotionOptions = {
  language: string;
  rtl: boolean;
  scope: RefObject<HTMLElement>;
};

type MotionConditions = {
  isDesktop: boolean;
  isMobile: boolean;
  reduceMotion: boolean;
};

const GROUP_VARIANTS: Record<string, { y: number; scale: number; stagger: number }> = {
  'fade-intro': { y: 0, scale: 1, stagger: 0.1 },
  fade: { y: 0, scale: 1, stagger: 0.1 },
  stats: { y: 14, scale: 1, stagger: 0.09 },
  features: { y: 0, scale: 1, stagger: 0.1 },
  integrations: { y: 10, scale: 1, stagger: 0.055 },
  differentiators: { y: 18, scale: 0.995, stagger: 0.1 },
  pricing: { y: 20, scale: 0.99, stagger: 0.1 },
  changelog: { y: 16, scale: 1, stagger: 0.11 },
  faq: { y: 14, scale: 1, stagger: 0.09 },
  cta: { y: 18, scale: 0.995, stagger: 0.09 },
};

export function useLandingMotion({ language, rtl, scope }: LandingMotionOptions) {
  useGSAP(
    (context, contextSafe) => {
      const select = context.selector;
      if (!select) return;

      const first = <T extends Element>(query: string) => select(query)[0] as T | undefined;
      const all = <T extends Element>(query: string) => select(query) as T[];
      const media = gsap.matchMedia();

      media.add(
        {
          isDesktop: '(min-width: 768px)',
          isMobile: '(max-width: 767px)',
          reduceMotion: '(prefers-reduced-motion: reduce)',
        },
        (mediaContext) => {
          const { isDesktop, isMobile, reduceMotion } =
            mediaContext.conditions as MotionConditions;

          if (reduceMotion) {
            return;
          }

          const interactionCleanups: Array<() => void> = [];
          const hero = first<HTMLElement>('[data-motion="hero"]');
          const heroCopy = first<HTMLElement>('[data-motion="hero-copy"]');
          const heroGlow = first<HTMLElement>('[data-motion="hero-glow"]');
          const heroBadge = first<HTMLElement>('[data-motion="hero-badge"]');
          const heroTitleFirst = first<HTMLElement>('[data-motion="hero-title-first"]');
          const heroTitleHighlight = first<HTMLElement>('[data-motion="hero-title-highlight"]');
          const heroDescription = first<HTMLElement>('[data-motion="hero-description"]');
          const heroActions = first<HTMLElement>('[data-motion="hero-actions"]');
          const heroButtons = heroActions
            ? Array.from(
                heroActions.querySelectorAll<HTMLElement>('[data-motion-hero-button]'),
              )
            : [];
          const heroReassurance = first<HTMLElement>('[data-motion="hero-reassurance"]');
          const dashboardShell = first<HTMLElement>('[data-motion="hero-dashboard-shell"]');
          const dashboard = first<HTMLElement>('[data-motion="hero-dashboard"]');
          const dashboardMetrics = all<HTMLElement>('[data-motion="dashboard-metric"]');
          const chartLine = first<SVGPathElement>('[data-motion="dashboard-chart-line"]');
          const chartBars = all<HTMLElement>('[data-motion="dashboard-chart-bar"]');

          const intro = gsap.timeline({
            defaults: { duration: 0.76, ease: 'power2.out' },
          });

          if (heroBadge) {
            intro.from(heroBadge, { autoAlpha: 0, y: 12, duration: 0.48 });
          }

          if (heroTitleFirst) {
            intro.from(heroTitleFirst, { autoAlpha: 0, y: isMobile ? 20 : 30 }, '-=0.14');
          }

          if (heroTitleHighlight) {
            intro.from(
              heroTitleHighlight,
              { autoAlpha: 0, duration: 0.82 },
              '-=0.58',
            );
          }

          if (heroDescription) {
            intro.from(heroDescription, { autoAlpha: 0, y: 16, duration: 0.68 }, '-=0.4');
          }

          if (heroButtons.length) {
            intro.fromTo(
              heroButtons,
              {
                x: (index) => (index === 0 ? (rtl ? 12 : -12) : (rtl ? -12 : 12)),
                y: 16,
                scale: 0.94,
                rotation: (index) => (index === 0 ? -0.8 : 0.8),
                transformOrigin: 'center center',
              },
              {
                x: 0,
                y: 0,
                scale: 1,
                rotation: 0,
                duration: 0.72,
                ease: 'power3.out',
                stagger: 0.1,
                clearProps: 'transform',
              },
              0.18,
            );
            intro.to(
              heroButtons,
              {
                autoAlpha: 1,
                duration: 0.62,
                ease: 'power2.out',
                stagger: 0.1,
              },
              0.18,
            );

            const primaryButton = heroButtons.find(
              (button) => button.dataset.motionHeroButton === 'primary',
            );
            const secondaryButton = heroButtons.find(
              (button) => button.dataset.motionHeroButton === 'secondary',
            );
            const primaryArrow = primaryButton?.querySelector<SVGElement>(
              '[data-motion-hero-button-arrow]',
            );

            if (primaryButton) {
              intro.fromTo(
                primaryButton,
                { boxShadow: '0 0 0 0 rgba(162,84,79,0)' },
                {
                  boxShadow:
                    '0 0 0 7px rgba(162,84,79,0.13), 0 12px 32px rgba(162,84,79,0.28)',
                  duration: 0.42,
                  ease: 'power2.out',
                  repeat: 1,
                  yoyo: true,
                  clearProps: 'boxShadow',
                },
                0.92,
              );
            }

            if (primaryArrow) {
              intro.to(
                primaryArrow,
                {
                  x: rtl ? -6 : 6,
                  duration: 0.32,
                  ease: 'power2.inOut',
                  repeat: 1,
                  yoyo: true,
                  clearProps: 'transform',
                },
                0.94,
              );
            }

            if (secondaryButton) {
              intro.fromTo(
                secondaryButton,
                { boxShadow: '0 0 0 0 rgba(240,237,230,0)' },
                {
                  boxShadow: '0 0 0 6px rgba(240,237,230,0.07)',
                  duration: 0.38,
                  ease: 'power2.out',
                  repeat: 1,
                  yoyo: true,
                  clearProps: 'boxShadow',
                },
                1.02,
              );
            }

            const safe = contextSafe ?? (<T extends Function>(callback: T) => callback);

            heroButtons.forEach((button) => {
              const isPrimary = button.dataset.motionHeroButton === 'primary';
              const arrow = button.querySelector<SVGElement>(
                '[data-motion-hero-button-arrow]',
              );
              let hovered = false;

              const animateRestingState = safe(() => {
                gsap.killTweensOf([button, arrow].filter(Boolean));
                gsap.to(button, {
                  y: hovered ? -4 : 0,
                  scale: hovered ? (isPrimary ? 1.045 : 1.025) : 1,
                  boxShadow: hovered
                    ? isPrimary
                      ? '0 14px 34px rgba(162,84,79,0.34)'
                      : '0 12px 28px rgba(0,0,0,0.3)'
                    : '0 0 0 rgba(0,0,0,0)',
                  duration: hovered ? 0.3 : 0.42,
                  ease: 'power2.out',
                });

                if (arrow) {
                  gsap.to(arrow, {
                    x: hovered ? (rtl ? -5 : 5) : 0,
                    duration: hovered ? 0.3 : 0.38,
                    ease: 'power2.out',
                  });
                }
              });

              const onPointerEnter = safe(() => {
                hovered = true;
                animateRestingState();
              });
              const onPointerLeave = safe(() => {
                hovered = false;
                animateRestingState();
              });
              const onFocus = safe(() => {
                hovered = true;
                animateRestingState();
              });
              const onBlur = safe(() => {
                hovered = button.matches(':hover');
                animateRestingState();
              });
              const onPointerDown = safe(() => {
                gsap.killTweensOf(button);
                gsap.to(button, {
                  y: hovered ? -1 : 1,
                  scale: 0.965,
                  duration: 0.1,
                  ease: 'power2.out',
                });
              });
              const onPointerUp = safe(() => animateRestingState());

              if (isDesktop) {
                button.addEventListener('pointerenter', onPointerEnter);
                button.addEventListener('pointerleave', onPointerLeave);
              }
              button.addEventListener('focus', onFocus);
              button.addEventListener('blur', onBlur);
              button.addEventListener('pointerdown', onPointerDown);
              button.addEventListener('pointerup', onPointerUp);
              button.addEventListener('pointercancel', onPointerUp);

              interactionCleanups.push(() => {
                if (isDesktop) {
                  button.removeEventListener('pointerenter', onPointerEnter);
                  button.removeEventListener('pointerleave', onPointerLeave);
                }
                button.removeEventListener('focus', onFocus);
                button.removeEventListener('blur', onBlur);
                button.removeEventListener('pointerdown', onPointerDown);
                button.removeEventListener('pointerup', onPointerUp);
                button.removeEventListener('pointercancel', onPointerUp);
                gsap.killTweensOf([button, arrow].filter(Boolean));
              });
            });
          }

          if (heroReassurance) {
            intro.from(heroReassurance, { autoAlpha: 0, y: 8, duration: 0.46 }, '-=0.24');
          }

          if (dashboard) {
            intro.from(
              dashboard,
              {
                autoAlpha: 0,
                y: isMobile ? 26 : 42,
                scale: isMobile ? 0.99 : 0.98,
                rotateX: isDesktop ? 3.5 : 0,
                transformPerspective: 1000,
                transformOrigin: 'center top',
                duration: isMobile ? 0.72 : 0.82,
              },
              0.52,
            );
          }

          if (dashboardMetrics.length) {
            intro.from(
              dashboardMetrics,
              { autoAlpha: 0, y: 8, duration: 0.38, stagger: 0.05 },
              0.82,
            );
          }

          if (chartLine) {
            const lineLength = chartLine.getTotalLength();
            gsap.set(chartLine, {
              strokeDasharray: lineLength,
              strokeDashoffset: lineLength,
            });
            intro.to(
              chartLine,
              { strokeDashoffset: 0, duration: 0.7, ease: 'power2.inOut' },
              0.9,
            );
          }

          if (chartBars.length) {
            intro.from(
              chartBars,
              {
                scaleX: 0,
                transformOrigin: rtl ? 'right center' : 'left center',
                duration: 0.42,
                stagger: 0.05,
              },
              0.86,
            );
          }

          if (hero && heroCopy && dashboardShell && heroGlow) {
            gsap
              .timeline({
                scrollTrigger: {
                  trigger: hero,
                  start: 'top top',
                  end: 'bottom top',
                  scrub: isDesktop ? 1 : 1.15,
                  invalidateOnRefresh: true,
                },
              })
              .to(
                heroCopy,
                {
                  y: isDesktop ? -42 : -20,
                  autoAlpha: isDesktop ? 0.45 : 0.7,
                  ease: 'none',
                },
                0,
              )
              .to(
                dashboardShell,
                {
                  y: isDesktop ? -28 : -14,
                  scale: isDesktop ? 0.985 : 0.995,
                  transformOrigin: 'center top',
                  ease: 'none',
                },
                0,
              )
              .to(
                heroGlow,
                {
                  y: isDesktop ? 100 : 52,
                  scale: isDesktop ? 1.13 : 1.06,
                  autoAlpha: 0.08,
                  ease: 'none',
                },
                0,
              );
          }

          all<HTMLElement>('[data-motion="section-heading"]').forEach((heading) => {
            const fadeOnly = heading.closest('#features') !== null;

            gsap.fromTo(
              heading,
              {
                autoAlpha: 0,
                y: fadeOnly ? 0 : isMobile ? 18 : 26,
              },
              {
                autoAlpha: 1,
                y: 0,
                duration: isMobile ? 0.96 : 1.12,
                ease: 'power2.out',
                clearProps: 'opacity,visibility,transform',
                scrollTrigger: {
                  trigger: heading,
                  start: isMobile ? 'top 95%' : 'top 93%',
                  once: true,
                },
              },
            );
          });

          all<HTMLElement>('[data-motion-group]').forEach((group) => {
            const groupName = group.dataset.motionGroup ?? '';
            const isIntroFade = groupName === 'fade-intro';

            const variant = GROUP_VARIANTS[groupName] ?? {
              y: 18,
              scale: 0.995,
              stagger: 0.08,
            };
            const items = Array.from(
              group.querySelectorAll<HTMLElement>('[data-motion-item]'),
            );

            if (!items.length) return;

            gsap.fromTo(
              items,
              {
                autoAlpha: 0,
                y: isMobile ? Math.min(variant.y, 16) : variant.y,
                scale: isMobile ? Math.max(variant.scale, 0.995) : variant.scale,
              },
              {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: isMobile ? 0.88 : 1.08,
                ease: 'power2.out',
                stagger: isMobile ? Math.min(variant.stagger, 0.08) : variant.stagger,
                clearProps: 'opacity,visibility,transform',
                delay: isIntroFade ? 0.18 : 0,
                scrollTrigger: isIntroFade
                  ? undefined
                  : {
                      trigger: group,
                      start: isMobile ? 'top 95%' : 'top 93%',
                      once: true,
                    },
              },
            );
          });

          const changelogGroup = first<HTMLElement>('[data-motion-group="changelog"]');
          const changelogLines = all<HTMLElement>('[data-motion-line="changelog"]');
          if (changelogGroup && changelogLines.length) {
            gsap.from(changelogLines, {
              scaleY: 0,
              transformOrigin: 'top center',
              duration: 1.1,
              ease: 'power2.inOut',
              immediateRender: false,
              stagger: 0.11,
              scrollTrigger: {
                trigger: changelogGroup,
                start: isMobile ? 'top 95%' : 'top 93%',
                once: true,
              },
            });
          }

          return () => {
            interactionCleanups.forEach((cleanup) => cleanup());
          };
        },
      );

      const refreshFrame = window.requestAnimationFrame(() => ScrollTrigger.refresh());

      return () => {
        window.cancelAnimationFrame(refreshFrame);
        media.revert();
      };
    },
    {
      scope,
      dependencies: [language, rtl],
      revertOnUpdate: true,
    },
  );
}
